import { UNIT_CATALOG } from '../engine/unit-catalog';
import type { MapCurve, ModRoute, ModSource, ProgramConfig, SlotConfig } from '../engine/protocol';
import type { Fidelity, ProgramEntry, ResolvedProgram } from './schema';
import { PROGRAM_OVERRIDES } from './overrides';

/**
 * Turns a program-list entry (axis labels + FX chain, straight from Korg's
 * published program list) into a concrete ProgramConfig for the engine.
 *
 * Korg documents WHAT each axis controls but not the numeric mapping, so this
 * is heuristic: an ordered rule table matches axis labels to unit parameters.
 * PROGRAM_OVERRIDES hand-tunes individual programs on top; add overrides as
 * programs get played and compared against the hardware.
 */

interface RuleTarget {
  unit: string;
  param: string;
  min?: number;
  max?: number;
  curve?: MapCurve;
  /** Which instance of `unit` in the chain (1-based). */
  occ?: number;
  /** Slot params to preset when this rule fires (e.g. HPF ⇒ mode=1). */
  presets?: Record<string, number>;
}

interface Rule {
  re: RegExp;
  targets: RuleTarget[];
}

const T = (
  unit: string,
  param: string,
  extra: Partial<RuleTarget> = {}
): RuleTarget => ({ unit, param, ...extra });

/**
 * Ordered: first textual+chain match wins for a label. Specific before
 * generic. All matching is case-insensitive on the whole label part.
 */
const AXIS_RULES: Rule[] = [
  // --- Filter family -----------------------------------------------------
  { re: /^lpf cutoff$|^cutoff frequency$|^filter cutoff$/i, targets: [T('Filter', 'cutoff'), T('Delay', 'tone')] },
  { re: /^hpf cutoff$/i, targets: [T('Filter', 'cutoff', { presets: { mode: 1 } }), T('Delay', 'tone', { curve: 'invLin' })] },
  { re: /^bpf cutoff$/i, targets: [T('Filter', 'cutoff', { presets: { mode: 0.5 } })] },
  { re: /resonance$/i, targets: [T('Filter', 'reso', { max: 0.85 }), T('Phaser', 'reso')] },
  { re: /^1st formant$/i, targets: [T('Filter', 'cutoff', { min: 0.25, max: 0.55, presets: { mode: 0.5, reso: 0.75 } })] },
  { re: /^2nd formant$/i, targets: [T('Filter', 'cutoff', { min: 0.45, max: 0.8, occ: 2, presets: { mode: 0.5, reso: 0.75 } })] },
  { re: /input sensitivity$/i, targets: [T('Filter', 'sens', { min: 0.5, max: 1 })] },
  { re: /^(lpf|hpf|filter) lfo speed$/i, targets: [T('Filter', 'lfoSpeed')] },

  // --- Isolator ----------------------------------------------------------
  { re: /^low - (mid - )?high$|^low - mid$/i, targets: [T('Isolator', 'sweep')] },
  { re: /^isolator cutoff$/i, targets: [T('Isolator', 'cutoff')] },
  { re: /^isolator depth$/i, targets: [T('Isolator', 'depth')] },

  // --- Looper ------------------------------------------------------------
  { re: /^looper beat( l| 2)?$/i, targets: [T('Looper', 'beat')] },
  { re: /^direction \(switch\)$/i, targets: [T('Looper', 'direction', { curve: 'switch' })] },
  { re: /^direction( 2)?$|^direction \(crossfade\)$/i, targets: [T('Looper', 'direction')] },
  { re: /^looper speed( & direction)?( l)?$/i, targets: [T('Looper', 'speed')] },
  { re: /^(looper|reverse) probability$/i, targets: [T('Looper', 'probability', { min: 1, max: 0.3 })] },
  { re: /^slicer length$|^normal - triplet$/i, targets: [T('Looper', 'beat')] },

  // --- Vinyl break ---------------------------------------------------------
  { re: /^break time$/i, targets: [T('Vinyl Break', 'breakTime', { presets: { engage: 1 } })] },
  { re: /^scratch( width)?$/i, targets: [T('Vinyl Break', 'scratch')] },

  // --- Delay / tape echo ---------------------------------------------------
  { re: /^delay time$/i, targets: [T('Delay', 'time'), T('Tape Echo', 'time'), T('Flanger', 'time'), T('Pitch Shifter', 'window')] },
  { re: /^delay (depth|feedback)$/i, targets: [T('Delay', 'depth', { min: 0.15 }), T('Tape Echo', 'depth', { min: 0.15 })] },
  { re: /^delay tone$/i, targets: [T('Delay', 'tone'), T('Tape Echo', 'tone')] },
  { re: /^l-r pan$/i, targets: [T('Tremolo', 'depth', { presets: { mode: 1 } })] },

  // --- Reverb / ER / Shimmer -------------------------------------------------
  { re: /^reverb time$/i, targets: [T('Reverb', 'time'), T('Shimmer', 'time'), T('Early Reflection', 'time')] },
  { re: /^reverb depth$/i, targets: [T('Reverb', 'depth', { min: 0.1 }), T('Shimmer', 'depth', { min: 0.1 }), T('Early Reflection', 'mix')] },
  { re: /^reverb tone$/i, targets: [T('Reverb', 'damp', { curve: 'invLin' }), T('Shimmer', 'damp', { curve: 'invLin' })] },
  { re: /^gate time$/i, targets: [T('Reverb', 'gate', { min: 0.25 })] },
  { re: /^reverb pan lfo speed$/i, targets: [T('Reverb', 'depth', { min: 0.2 }), T('Shimmer', 'depth', { min: 0.2 })] },
  { re: /^mix balance$/i, targets: [T('Early Reflection', 'mix'), T('Ring Mod', 'mix')] },

  // --- Distortion / color ----------------------------------------------------
  { re: /^distortion( tone)?$/i, targets: [T('Distortion', 'drive'), T('Tape Echo', 'saturation')] },
  { re: /^tone$/i, targets: [T('Distortion', 'tone')] },
  { re: /^(high|low) gain$/i, targets: [T('Distortion', 'drive', { min: 0.2 })] },
  { re: /^sampling frequency$/i, targets: [T('Decimator', 'rate', { min: 1, max: 0.05 })] },
  { re: /^sampling bit$/i, targets: [T('Decimator', 'bits', { min: 1, max: 0 })] },
  { re: /^decimator depth$/i, targets: [T('Decimator', 'depth')] },
  { re: /^decimator tone$/i, targets: [T('Decimator', 'tone')] },
  { re: /^ir depth$/i, targets: [T('IR Loader', 'depth')] },

  // --- Compressor -------------------------------------------------------------
  { re: /^attack$/i, targets: [T('Compressor', 'attack')] },
  { re: /^pump depth$/i, targets: [T('Compressor', 'depth', { min: 0.3 }), T('Reverb', 'depth', { min: 0.2 })] },

  // --- Ring mod ----------------------------------------------------------------
  { re: /^ring mod frequency$/i, targets: [T('Ring Mod', 'freq')] },
  { re: /^ring mod (mix balance|pan)$/i, targets: [T('Ring Mod', 'mix')] },

  // --- Pitch shifter / vocal ----------------------------------------------------
  { re: /^pitch shift$/i, targets: [T('Pitch Shifter', 'pitch', { min: 0.25, max: 0.75 })] },
  { re: /^pitch shift tracking$/i, targets: [T('Pitch Shifter', 'tracking')] },
  { re: /^harmony (change|pitch)( 2)?$/i, targets: [T('Vocal FX', 'harmony'), T('Pitch Shifter', 'pitch', { min: 0.4, max: 0.7 }), T('Analog Osc', 'pitch', { min: 0.3, max: 0.6 })] },
  { re: /^formant shift( 2)?$/i, targets: [T('Vocal FX', 'formant')] },
  { re: /^root change/i, targets: [T('Vocal FX', 'root')] },

  // --- Resonators / drone -------------------------------------------------------
  { re: /^resonator tone$/i, targets: [T('Chord Resonator', 'tone'), T('Drone String', 'tone')] },
  { re: /^resonator decay time$/i, targets: [T('Chord Resonator', 'decay'), T('Drone String', 'decay')] },
  { re: /^resonator pitch$/i, targets: [T('Chord Resonator', 'key')] },
  { re: /^resonator lfo speed$/i, targets: [T('Drone String', 'lfoSpeed')] },

  // --- Flanger / phaser / chorus -----------------------------------------------------
  { re: /^flanger delay time$/i, targets: [T('Flanger', 'time')] },
  { re: /^flanger (lfo )?speed$/i, targets: [T('Flanger', 'speed')] },
  { re: /^flanger (lfo )?depth$/i, targets: [T('Flanger', 'depth')] },
  { re: /^flanger feedback$/i, targets: [T('Flanger', 'feedback', { max: 0.9 })] },
  { re: /^phaser (lfo )?speed$/i, targets: [T('Phaser', 'speed', { presets: { depth: 0.6 } })] },
  { re: /^phaser (lfo depth|depth)( & resonance)?$/i, targets: [T('Phaser', 'depth')] },
  { re: /^phaser frequency$/i, targets: [T('Phaser', 'freq')] },
  { re: /^phaser resonance$/i, targets: [T('Phaser', 'reso')] },
  { re: /^frequency \(stepped\)$/i, targets: [T('Phaser', 'freq', { presets: { step: 1 } })] },
  { re: /^frequency( \(continuous\))?$/i, targets: [T('Phaser', 'freq'), T('Ring Mod', 'freq')] },
  { re: /^chorus (lfo )?speed$/i, targets: [T('Chorus', 'speed')] },
  { re: /^chorus depth$/i, targets: [T('Chorus', 'depth')] },

  // --- Tremolo / slicer / pan ----------------------------------------------------------
  { re: /^slicer lfo speed$/i, targets: [T('Tremolo', 'speed', { presets: { mode: 0.5, shape: 0.5 } })] },
  { re: /^slicer lfo length$/i, targets: [T('Tremolo', 'length', { presets: { mode: 0.5, shape: 0.5 } })] },
  { re: /^(pan|pumper) lfo speed$/i, targets: [T('Tremolo', 'speed', { presets: { mode: 1 } }), T('Ring Mod', 'lfoSpeed'), T('Filter', 'lfoSpeed'), T('Flanger', 'speed')] },
  { re: /^reverb pan lfo speed$/i, targets: [T('Reverb', 'depth', { min: 0.2 }), T('Tremolo', 'speed', { presets: { mode: 1 } })] },
  { re: /^tremolo speed$/i, targets: [T('Tremolo', 'speed'), T('Filter', 'lfoSpeed', { presets: { lfoDepth: 0.5 } })] },
  { re: /^tremolo depth$/i, targets: [T('Tremolo', 'depth')] },
  { re: /^amp - pan$/i, targets: [T('Tremolo', 'mode')] },

  // --- Grain shifter ---------------------------------------------------------------------
  { re: /^grain cycle speed( r)?( & length)?$/i, targets: [T('Grain Shifter', 'cycle', { min: 1, max: 0 })] },
  { re: /^grain length( r)?$/i, targets: [T('Grain Shifter', 'length')] },

  // --- Synth / drum ------------------------------------------------------------------------
  { re: /^synth pitch( & lfo speed)?( input sensitivity)?$/i, targets: [T('Analog Osc', 'pitch', { presets: { trigger: 0, level: 0.7 } })] },
  { re: /^synth (tone|change & pitch|change etc\.|transpose)( & noise level)?( & cross mod)?$/i, targets: [T('Analog Osc', 'tone')] },
  { re: /^(cross mod|synth cross mod)$/i, targets: [T('Analog Osc', 'crossMod')] },
  { re: /^synth pitch octave & cross mod$/i, targets: [T('Analog Osc', 'crossMod')] },
  { re: /^kick - (tom - )?snare$/i, targets: [T('Analog Osc', 'pitch', { min: 0.05, max: 0.35 })] },
  { re: /^drum pitch$/i, targets: [T('Sample Osc', 'pitch', { min: 0.3, max: 0.7 }), T('Analog Osc', 'pitch', { min: 0.05, max: 0.4 })] },
  { re: /^decay time$/i, targets: [T('Analog Osc', 'decay')] },
  { re: /^noise tone$/i, targets: [T('Sample Osc', 'tone'), T('Analog Osc', 'tone')] },
  { re: /^pitch (lfo speed|decay time)$/i, targets: [T('Analog Osc', 'crossMod')] },
  { re: /^pitch lfo depth$/i, targets: [T('Pitch Shifter', 'lfoDepth')] },
  { re: /^synth change$/i, targets: [T('Analog Osc', 'tone')] },
  { re: /^pitch$/i, targets: [T('Analog Osc', 'pitch'), T('Pitch Shifter', 'pitch', { min: 0.25, max: 0.75 })] },
  { re: /^level$/i, targets: [T('Analog Osc', 'level'), T('Sample Osc', 'level'), T('Isolator', 'level')] },

  // --- Generic fallbacks (searched last, resolved by chain contents) ---------------------------
  { re: /^lfo speed( 1| 2)?$/i, targets: [T('Filter', 'lfoSpeed'), T('Tremolo', 'speed'), T('Flanger', 'speed'), T('Phaser', 'speed'), T('Pitch Shifter', 'lfoSpeed', { presets: { lfoDepth: 0.5 } }), T('Looper', 'speed')] },
  { re: /^lfo (depth|modulation)( & resonance)?$/i, targets: [T('Filter', 'lfoDepth', { max: 0.7 }), T('Tremolo', 'depth'), T('Flanger', 'depth'), T('Phaser', 'depth'), T('Pitch Shifter', 'lfoDepth')] },
  { re: /^lfo shape$/i, targets: [T('Filter', 'lfoShape')] },
  { re: /^lfo phase shift$/i, targets: [T('Looper', 'speed')] },
  { re: /^feedback$/i, targets: [T('Flanger', 'feedback', { max: 0.9 }), T('Delay', 'depth'), T('Tape Echo', 'depth')] },
  { re: /^depth$/i, targets: [T('Isolator', 'depth'), T('Compressor', 'depth'), T('Tremolo', 'depth'), T('Chord Resonator', 'depth'), T('Reverb', 'depth')] },
  { re: /^cutoff frequency$/i, targets: [T('Isolator', 'cutoff')] },
  { re: /^scratch$/i, targets: [T('Vinyl Break', 'scratch')] },
];

/** Touch-2 engagement: label ⇒ raise a "presence" parameter while touched. */
const ENGAGE_RULES: Rule[] = [
  { re: /vinyl break|^reverse( mix)?$|backspin/i, targets: [T('Vinyl Break', 'engage', { curve: 'switch' }), T('Looper', 'direction', { min: 1, max: 0, curve: 'switch' })] },
  { re: /looper( 2)?( beat)?$/i, targets: [T('Looper', 'on', { curve: 'switch' })] },
  { re: /delay|echo/i, targets: [T('Delay', 'depth', { max: 0.55 }), T('Tape Echo', 'depth', { max: 0.55 })] },
  { re: /gate disable/i, targets: [T('Reverb', 'gate', { min: 0.5, max: 0, curve: 'switch' })] },
  { re: /dry isolation/i, targets: [T('Isolator', 'depth', { occ: 2 })] },
  { re: /reverb/i, targets: [T('Reverb', 'depth', { max: 0.6 }), T('Shimmer', 'depth', { max: 0.6 })] },
  { re: /distortion/i, targets: [T('Distortion', 'drive', { max: 0.6 })] },
  { re: /decimator|sampling bit/i, targets: [T('Decimator', 'depth')] },
  { re: /ring mod/i, targets: [T('Ring Mod', 'mix')] },
  { re: /flanger/i, targets: [T('Flanger', 'depth', { max: 0.7 })] },
  { re: /grain shifter/i, targets: [T('Grain Shifter', 'mix')] },
  { re: /lfo (modulation|speed)/i, targets: [T('Filter', 'lfoDepth', { max: 0.6 })] },
  { re: /2nd band/i, targets: [T('Isolator', 'depth', { occ: 2 })] },
  { re: /hpf & decimator/i, targets: [T('Decimator', 'depth')] },
  { re: /pitch (envelope|lfo)/i, targets: [T('Analog Osc', 'crossMod', { max: 0.5 })] },
  { re: /fx change/i, targets: [T('Tremolo', 'depth', { max: 0.8 })] },
  { re: /shimmer/i, targets: [T('Shimmer', 'depth', { max: 0.7 })] },
  { re: /chord resonator/i, targets: [T('Chord Resonator', 'depth')] },
  { re: /^dry mute/i, targets: [T('Grain Shifter', 'mix'), T('Looper', 'on', { curve: 'switch' }), T('Shimmer', 'depth')] },
];

const SOURCE_OF: Record<'x' | 'y' | 'x2' | 'y2', ModSource> = {
  x: 'x1',
  y: 'y1',
  x2: 'x2',
  y2: 'y2',
};

function splitLabel(label: string): string[] {
  // "Low - Mid - High" must survive; split only on comma and " & ".
  return label
    .split(/,| & /)
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && s !== '---');
}

/** Find the chain slot index for a rule target, honoring occurrence. */
function findSlot(chain: string[], target: RuleTarget, labelWants2nd: boolean): number {
  const occWanted = target.occ ?? (labelWants2nd ? 2 : 1);
  let seen = 0;
  for (let i = 0; i < chain.length; i++) {
    if (chain[i] === target.unit) {
      seen++;
      if (seen === occWanted) return i;
    }
  }
  // fall back to first instance if the requested occurrence doesn't exist
  return occWanted > 1 ? chain.indexOf(target.unit) : -1;
}

interface ResolveResult {
  routes: ModRoute[];
  presets: Array<{ slot: number; params: Record<string, number> }>;
  resolved: boolean;
}

function resolveLabel(
  label: string,
  source: ModSource,
  chain: string[],
  rules: Rule[]
): ResolveResult {
  const out: ResolveResult = { routes: [], presets: [], resolved: false };
  const wants2nd = /\b2(nd)?\b/.test(label) && !/\bpitch shift\b/i.test(label);
  for (const part of splitLabel(label)) {
    let partResolved = false;
    for (const rule of rules) {
      if (!rule.re.test(part)) continue;
      for (const target of rule.targets) {
        const slot = findSlot(chain, target, wants2nd);
        if (slot < 0) continue;
        out.routes.push({
          source,
          slot,
          param: target.param,
          min: target.min ?? 0,
          max: target.max ?? 1,
          curve: target.curve ?? 'lin',
        });
        if (target.presets) out.presets.push({ slot, params: target.presets });
        partResolved = true;
        break; // first present target wins for this rule
      }
      if (partResolved) break; // first matching rule with a present unit wins
    }
    if (partResolved) out.resolved = true;
  }
  return out;
}

export interface MappedProgram {
  entry: ProgramEntry;
  config: ProgramConfig;
  resolved: ResolvedProgram;
}

export function mapProgram(entry: ProgramEntry): MappedProgram {
  const chain = entry.fxChain;
  const missingUnits = chain.filter((u) => !(UNIT_CATALOG[u]?.implemented ?? false));
  const unresolvedAxes: string[] = [];

  // Slots: every chain unit gets catalog defaults (stubs included — the
  // worklet drops them; slot indices stay aligned with the printed chain).
  const slots: SlotConfig[] = chain.map((unit) => {
    const spec = UNIT_CATALOG[unit];
    const params: Record<string, number> = {};
    if (spec) {
      for (const [name, p] of Object.entries(spec.params)) params[name] = p.def;
      if ('sync' in spec.params) params['sync'] = entry.bpmSync ? 1 : 0;
    }
    return { unit, params };
  });

  const routes: ModRoute[] = [];
  const applyResult = (label: string, r: ResolveResult): void => {
    if (!r.resolved) {
      unresolvedAxes.push(label);
      return;
    }
    routes.push(...r.routes);
    for (const preset of r.presets) {
      const slot = slots[preset.slot];
      if (slot) Object.assign(slot.params, preset.params);
    }
  };

  applyResult(`X:${entry.xAxis}`, resolveLabel(entry.xAxis, SOURCE_OF.x, chain, AXIS_RULES));
  applyResult(`Y:${entry.yAxis}`, resolveLabel(entry.yAxis, SOURCE_OF.y, chain, AXIS_RULES));

  if (entry.touch2) {
    const { label, x2Axis, y2Axis } = entry.touch2;
    if (label) {
      const engage = resolveLabel(label, 'touch2', chain, ENGAGE_RULES);
      if (engage.resolved) {
        // While untouched, the engaged parameter must rest at its route min.
        for (const r of engage.routes) {
          const slot = slots[r.slot];
          if (slot) slot.params[r.param] = r.min;
        }
        routes.push(...engage.routes);
        for (const preset of engage.presets) {
          const slot = slots[preset.slot];
          if (slot) Object.assign(slot.params, preset.params);
        }
      } else {
        unresolvedAxes.push(`T2:${label}`);
      }
    }
    if (x2Axis) applyResult(`X2:${x2Axis}`, resolveLabel(x2Axis, SOURCE_OF.x2, chain, AXIS_RULES));
    if (y2Axis) applyResult(`Y2:${y2Axis}`, resolveLabel(y2Axis, SOURCE_OF.y2, chain, AXIS_RULES));
  }

  // Touch-engaged units: a Looper only rolls (and a Vinyl Break only brakes)
  // from the moment you touch — the freeze/brake needs a fresh 0→1 edge per
  // touch, exactly like the hardware. Any such slot nobody else engages gets
  // driven by touch 1 itself.
  slots.forEach((slot, i) => {
    if (slot.unit === 'Looper' && !routes.some((r) => r.slot === i && r.param === 'on')) {
      slot.params['on'] = 0;
      routes.push({ source: 'touch1', slot: i, param: 'on', min: 0, max: 1, curve: 'switch' });
    }
    if (slot.unit === 'Vinyl Break' && !routes.some((r) => r.slot === i && r.param === 'engage')) {
      slot.params['engage'] = 0;
      routes.push({ source: 'touch1', slot: i, param: 'engage', min: 0, max: 1, curve: 'switch' });
    }
  });

  let config: ProgramConfig = {
    id: entry.id,
    name: entry.name,
    slots,
    routes,
    bpmSync: entry.bpmSync,
    touchGated: true,
    releaseTime: 0.18,
  };

  // Hand-tuned corrections win over heuristics.
  const override = PROGRAM_OVERRIDES[entry.id];
  if (override) {
    config = override(config, entry);
    // Overrides may resolve axes the heuristics couldn't: any source that now
    // has at least one route no longer counts as unresolved.
    const covered = new Set(config.routes.map((r) => r.source));
    const prefixOf: Record<string, ModSource> = { X: 'x1', Y: 'y1', 'X2': 'x2', 'Y2': 'y2', 'T2': 'touch2' };
    for (let i = unresolvedAxes.length - 1; i >= 0; i--) {
      const prefix = unresolvedAxes[i]!.split(':', 1)[0]!;
      const src = prefixOf[prefix];
      if (src && covered.has(src)) unresolvedAxes.splice(i, 1);
    }
  }

  const implementedCount = chain.length - missingUnits.length;
  const fidelity: Fidelity =
    missingUnits.length === 0 && unresolvedAxes.length === 0
      ? 'full'
      : implementedCount > 0 && config.routes.length > 0
        ? 'partial'
        : 'minimal';

  return {
    entry,
    config,
    resolved: { entry, fidelity, missingUnits, unresolvedAxes },
  };
}
